<template>
  <Modal
    :show="true"
    size="md"
    title="Sign In"
    @close="closeModal"
  >
    <!-- User Icon Slot -->
    <template #icon>
      <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
      </svg>
    </template>

    <!-- Form Section - Responsive Spacing -->
    <form @submit.prevent="handleLogin" class="space-y-4 sm:space-y-6" data-testid="login-form">
      <!-- Email Field -->
      <div class="mb-4 sm:mb-6">
        <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">
          Email Address
        </label>
        <Input
          id="email"
          v-model="email"
          type="email"
          required
          autocomplete="email"
          placeholder="Enter your email"
          :disabled="isLoading"
          size="lg"
          custom-class="login-input"
          data-testid="login-email-input"
        >
          <template #icon>
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"></path>
            </svg>
          </template>
        </Input>
      </div>

      <!-- Password Field -->
      <div class="mb-4 sm:mb-6">
        <label for="password" class="block text-sm font-semibold text-gray-700 mb-2">
          Password
        </label>
        <Input
          id="password"
          v-model="password"
          type="password"
          required
          autocomplete="current-password"
          placeholder="Enter your password"
          :disabled="isLoading"
          size="lg"
          custom-class="login-input"
          data-testid="login-password-input"
        >
          <template #icon>
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
          </template>
        </Input>
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        class="bg-red-50 border border-red-200 rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6"
        data-testid="login-error-message"
      >
        <div class="flex items-center">
          <svg class="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span class="text-red-600 text-sm font-medium">{{ errorMessage }}</span>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="mt-6 sm:mt-8">
        <Button
          type="submit"
          :disabled="isLoading || !email || !password"
          :loading="isLoading"
          variant="solid"
          size="lg"
          full-width
          custom-class="login-submit-btn"
          data-testid="login-submit-btn"
        >
          <template #icon>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
            </svg>
          </template>
          {{ isLoading ? 'Signing in...' : 'Sign In' }}
        </Button>
      </div>
    </form>

    <!-- Guest Checkout Option - Feature Flag Controlled -->
    <template #footer>
      <div v-if="shouldShowGuestCheckout">
        <Button
          @click="continueAsGuest"
          variant="themedSecondary"
          size="md"
          full-width
          data-testid="login-guest-btn"
        >
          <template #icon>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
            </svg>
          </template>
          Continue as Guest
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script>
import logger from '@/utils/logger'
import { mapFields } from 'vuex-map-fields'
import { mapGetters } from 'vuex'
import datamethods from '../../services/datamethods.js'
import Button from '../ui/Button.vue'
import Input from '../ui/Input.vue'
import Modal from '../ui/Modal.vue'

export default {
  name: 'LoginModal',
  components: {
    Button,
    Input,
    Modal
  },
  mixins: [datamethods],
  data() {
    return {
      email: '',
      password: '',
      errorMessage: '',
      isLoading: false
    }
  },
  computed: {
    ...mapFields([
      'username',
      'loggedIn',
      'loggedInUser',
      'currentWallet'
    ]),
    ...mapGetters('config', [
      'shouldShowGuestCheckout',
      'isGuestCheckoutEnabled',
      'isEmailVerificationEnabled'
    ])
  },
  methods: {
    async handleLogin() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        // Use existing login logic
        const req = {
          email: this.email,
          password: this.password
        }

        const response = await this.callAPI(req, 'walletloginuserlimit')

        if (response.Successful) {
          // Update store with user data
          this.$store.commit('setLoggedIn', true)
          this.$store.commit('setUser', response.user)
          this.$store.commit('updateField', { path: 'currentWallet', value: response.wallet })

          // Add user to API headers
          this.AddUserToAPI(response.user)

          // Refresh categories to show all available categories for logged-in user
          await this.$store.dispatch('fetchCategories')

          // Close modal and redirect to shop
          this.closeModalAndRedirect()

          // Show success message
          this.$store.commit('updateField', {
            path: 'message',
            value: `Welcome back, ${response.user.name}!`
          })
        } else {
          this.errorMessage = response.Message || 'Login failed. Please check your credentials.'
        }
      } catch (error) {
        this.errorMessage = 'An error occurred during login. Please try again.'
        logger.error('Login error:', error)
      } finally {
        this.isLoading = false
      }
    },

    closeModal() {
      this.$emit('close')
      this.resetForm()
    },

    closeModalAndRedirect() {
      // Close modal first
      this.$emit('close')
      this.resetForm()

      // Get stored redirect URL or default to shop
      const redirectUrl = this.$store.state.loginRedirectUrl || '/shop'

      // Clear the stored redirect URL
      this.$store.commit('updateField', { path: 'loginRedirectUrl', value: null })

      // Redirect to appropriate page and scroll to top
      this.$nextTick(() => {
        if (this.$route.path !== redirectUrl) {
          this.$router.push(redirectUrl).then(() => {
            window.scrollTo(0, 0)
          })
          logger.debug('🔄 Post-login redirect to:', redirectUrl)
        } else {
          // Already on target page, just scroll to top
          window.scrollTo(0, 0)
          logger.debug('✅ Already on target page:', redirectUrl)
        }
      })
    },

    continueAsGuest() {
      // Close modal first
      this.$emit('close')
      this.resetForm()

      // Redirect to shop page and scroll to top
      this.$nextTick(() => {
        if (this.$route.path !== '/shop') {
          this.$router.push('/shop').then(() => {
            window.scrollTo(0, 0)
          })
        } else {
          // Already on shop page, just scroll to top
          window.scrollTo(0, 0)
        }
      })
    },



    resetForm() {
      this.email = ''
      this.password = ''
      this.errorMessage = ''
      this.isLoading = false
    }
  },

  mounted() {
    // Focus on email field when modal opens
    this.$nextTick(() => {
      const emailInput = this.$el.querySelector('#email')
      if (emailInput) {
        emailInput.focus()
      }
    })
  }
}
</script>

<style scoped>
/* Blue theme modal styling - flat */
.modal-icon {
  background-color: var(--button-color, #035891);
}

.login-input:focus {
  outline: none;
  border-color: var(--header-color, #7EB3D7);
}

.login-submit-btn {
  background-color: var(--button-color, #0c85b6) !important;
  background-image: none !important;
  color: var(--button-text-color, white);
  border: none !important;
}

.login-submit-btn:hover:not(:disabled) {
  background-color: var(--button-hover, #0a6b94) !important;
  background-image: none !important;
}
</style>
